/**
 * @typedef {Object} ValidityState
 * @property {boolean} badInput - Indicates if the user has provided input that the browser is unable to convert.
 * @property {boolean} customError - Indicates if the element's custom validity message is set.
 * @property {boolean} patternMismatch - Indicates if the value does not match the specified pattern.
 * @property {boolean} rangeOverflow - Indicates if the value is greater than the maximum specified by the max attribute.
 * @property {boolean} rangeUnderflow - Indicates if the value is less than the minimum specified by the min attribute.
 * @property {boolean} stepMismatch - Indicates if the value does not fit the rules provided by the step attribute.
 * @property {boolean} tooLong - Indicates if the value exceeds the specified maxlength.
 * @property {boolean} tooShort - Indicates if the value is less than the specified minlength.
 * @property {boolean} typeMismatch - Indicates if the value is not in the required syntax (e.g., email).
 * @property {boolean} valueMissing - Indicates if a required value is missing.
 * @property {boolean} valid - Indicates if the element meets all its validation constraints.
 */
export default class wdkHintboxError extends HTMLElement {
	// Can also have server-error | frontend-error | null to indicate the errorType
	static get observedAttributes() {
		return ['show'];
	}

	async connectedCallback() {
		this.currentComponent = this;
		this.setAttribute('inert', 'true');

		const myHtml = `<style>
					:host {
						--maxHeight: 300px;
						display: flex;
						flex-shrink: 0;
						align-items: center;
						position: relative;
						color: var(--COLOR_support_white_default);
						border-radius: var(--RADIUS_S);
						height: auto;
						text-align: left;
						max-height: var(--maxHeight);
						opacity: 1;
					}
					
					/* *** skin error *** */
					:host([skin="error"]) {
						display: none;
						margin-top: 8px;
						font: var(--FONT_default_75);
						background-color: var(--COLOR_alert_error_light);
						color: var(--COLOR_alert_error_default);
						overflow: hidden;
						padding: 8px;
						opacity: 0;
						transition: opacity .2s linear, display .3s ease allow-discrete;
					}
					
					:host([skin="error"]) a {
						color: var(--COLOR_alert_error_default);
					}
										
					:host([skin="error"][show]) {
						display: flex;
						opacity: 1;
						
						@starting-style {
							opacity: 0;
						}
					}

					/* *** inFormElement on-brand *** */
					:host([skin="inFormElement"][on-brand]) {
						display: none;
						opacity: 0;
						padding: 4px 8px;
					    gap: 4px;
						overflow: hidden;
						font: var(--FONT_default_50);
						background-color: var(--COLOR_support_black_alpha_54);
						transition: max-height .3s linear, opacity .3s linear, display .3s ease allow-discrete;
					}

					:host([skin="inFormElement"][on-brand][show]) {
						opacity: 1;
						display: flex;
					    
						@starting-style {
							opacity: 0;
						}
					}
					
					:host([skin="inFormElement"][on-brand]) ::slotted([slot="icon"].wdk-icon) {
						width: 14px !important;
						height: 14px !important;
						fill: var(--COLOR_support_white_default) !important;
					}
					
					/* *** inFormElement NOT on-brand *** */
					:host([skin="inFormElement"]:not([on-brand])) {
						display: none;
						opacity: 0;
						overflow: hidden;
						margin: 0;
						padding: 8px;
						font: var(--FONT_default_75);
						color: var(--COLOR_alert_error_default);
						transition: opacity .3s linear, display .3s ease allow-discrete;
					}
					
					:host([skin="inFormElement"][show]:not([on-brand])) {
						display: flex;
						opacity: 1;
						
						@starting-style {
							opacity: 0;
						}
					}
										
					:host([skin="inFormElement"]:not([on-brand])) a {
						color: var(--COLOR_alert_error_default);
					}

					/* *** General *** */
					:host([skin="inFormElement"]:not([on-brand])) ::slotted([slot="icon"].wdk-icon),
					:host([skin="error"]) ::slotted([slot="icon"].wdk-icon) {
						position: absolute;
						left: 8px;
						top: 50%;
						margin-top: -16px;
						padding-right: 8px;
						width: 32px !important;
						height: 32px !important;
						fill: var(--COLOR_alert_error_default) !important;
					}
										
					:host([skin="inFormElement"]:not([on-brand])) slot,
					:host([skin="error"]) slot {
						display: inline-block;
						padding-left: 39px;
					}
					
					:host([skin="inFormElement"]:not([on-brand])) slot.is-empty,
					:host([skin="error"]) slot.is-empty {
						padding-left: 0;
					}
					
					:host .visuallyHidden {
						position: absolute !important;
						left: -10000px;
						top: auto;
						width: 1px;
						height: 1px;
						overflow: hidden;
						clip-path: inset(50%);
					}
					
					:host button {
						position: relative;
						border: none;
						background: transparent;
						cursor: pointer;
						align-self: flex-start;
    					margin: 0;
    					padding: 0;
    					width: 18px;
    					height: 18px;
					}
					
					:host button:focus-visible {
						outline: 2px dashed currentColor;
						outline-offset: 10px;
					}

					:host button:after {
						content: "";
    					position: absolute;
    					top: 50%;
    					left: 50%;
    					transform: translate(-50%, -50%);
    					height: 48px;
    					width: 48px;
					}

					:host button:hover {
						opacity: .7;
					}

					:host button svg {
						width: 16px;
						height: 16px;
						fill: var(--COLOR_gray_100);
					}
					
					#a11yWrapper {
						flex-grow: 1;
					}
					
					.wdkA11yRepeat {
						position:absolute;
						left:-9999px;
						width: 1px;
						height: 1px;
					}
				</style>
				<slot name="icon"></slot>
				<div id="a11yWrapper" aria-live="polite" aria-atomic="true"></div>
				${this.#getCloseButton()}
			`;
		const shadowTemplate = document.createElement('template');

		shadowTemplate.innerHTML = jPar.wdk.security.sanitizeEvilHtml(myHtml);
		const clone = document.importNode(shadowTemplate.content, true);
		if (!this.shadowRoot) {
			this.attachShadow({ mode: 'open' });
			this.shadowRoot.appendChild(clone);
		}

		this.fillA11yWrapper();
		this.handleEmptyIcon(); // Kann initial ja auch schon show sein

		this.shadowRoot.querySelector('.js-close')?.addEventListener('click', () => {
			this.hide();
		});
	}

	attributeChangedCallback(name) {
		if (name === 'show') {
			this.fillA11yWrapper();
		}
	}

	#getCloseButton() {
		if (this.getAttribute('closable') !== null) {
			return `<button part="base" class="js-close" type="button" role="button" aria-disabled="false" aria-label="Close" tabindex="0">
        			<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="24" height="24" viewBox="0 0 24 24">
						<path id="icon_x-a" fill-rule="evenodd" clip-rule="evenodd" d="M19.7044 18.2864C19.8936 18.4741 20 18.7295 20 18.996C20 19.2624 19.8936 19.5179 19.7044 19.7055C19.5168 19.8947 19.2614 20.0011 18.9949 20.0011C18.7285 20.0011 18.4731 19.8947 18.2854 19.7055L12 13.4194L5.71456 19.7055C5.52692 19.8947 5.27151 20.0011 5.00507 20.0011C4.73863 20.0011 4.48321 19.8947 4.29558 19.7055C4.10641 19.5179 4 19.2624 4 18.996C4 18.7295 4.10641 18.4741 4.29558 18.2864L10.581 12.0003L4.29558 5.71418C3.90375 5.3223 3.90375 4.68693 4.29558 4.29505C4.68742 3.90317 5.32272 3.90317 5.71456 4.29505L12 10.5812L18.2854 4.29505C18.6773 3.90317 19.3126 3.90317 19.7044 4.29505C20.0963 4.68693 20.0963 5.3223 19.7044 5.71418L13.419 12.0003L19.7044 18.2864Z"/>
					</svg>
      			</button>
				`;
		} else {
			return '';
		}
	}

	fillA11yWrapper() {
		const lightClone = document.importNode(this.querySelector('template').content, true);

		if (this.shadowRoot !== null) {
			this.shadowRoot.querySelector('#a11yWrapper').innerHTML = '';
			this.shadowRoot.querySelector('#a11yWrapper').appendChild(lightClone);

			this.handleReloadLink();
			this.triggerA11yRepeat();
			this.handleShow();
		}
	}

	handleEmptyIcon() {
		const hasEmptySlot = this.shadowRoot.querySelector('slot').assignedElements().length === 0;
		if (hasEmptySlot) {
			this.shadowRoot.querySelector('slot').classList.add('is-empty');
		} else {
			this.shadowRoot.querySelector('slot').classList.remove('is-empty');
		}
	}

	show(scrollIntoView = false) {
		this.setAttribute('show', 'true');

		if (scrollIntoView) {
			window.setTimeout(() => {
				this.scrollIntoView({ behavior: 'smooth', block: 'end' });
			}, 300); // after Animation... not very elegant but ok
		}
	}

	handleShow() {
		if (this.hasAttribute('show')) {
			this.removeAttribute('inert');

			this.dispatchEvent(
				new CustomEvent('wdk-hintbox-error', {
					bubbles: true,
					detail: { state: 'shown', element: this },
				}),
			);
		}
	}

	hide() {
		this.removeAttribute('show');
		this.setAttribute('inert', 'true');

		this.dispatchEvent(
			new CustomEvent('wdk-hintbox-error', {
				bubbles: true,
				detail: { state: 'hidden', element: this },
			}),
		);
	}

	isVisible() {
		return this.getAttribute('show') === 'true';
	}

	triggerA11yRepeat() {
		window.setTimeout(() => {
			this.shadowRoot.querySelector('.wdkA11yRepeat')?.remove();
			const myDiv = document.createElement('div');
			myDiv.classList.add('wdkA11yRepeat');
			myDiv.innerText = '!';
			this.shadowRoot.querySelector('#a11yWrapper').appendChild(myDiv);
		}, 200);
	}

	handleReloadLink() {
		this.shadowRoot.querySelectorAll('.js-reloadPage').forEach((reloadLink) => {
			reloadLink.addEventListener('click', (event) => {
				event.preventDefault();
				document.location.reload();
			});
		});
	}
}
